import {
	useImperativeHandle,
	useRef,
	useState,
	type FocusEvent,
	type Ref,
	type TextareaHTMLAttributes,
} from "react";
import MDEditor, {
	type ICommand,
	type RefMDEditor,
} from "@uiw/react-md-editor/nohighlight";
import { useTheme } from "@/context/ThemeContext";
import { cn } from "@/lib/utils";
import { EditorTabs, type EditorTab } from "./EditorTabs";
import { MarkdownToolbar } from "./MarkdownToolbar";
import { MarkdownPreview } from "./MarkdownPreview";
import { MarkdownFooter } from "./MarkdownFooter";
import { POPUP_ATTR } from "./ToolbarMenus";
import { SHORTCUT_COMMANDS, insertCommand } from "./markdown-commands";
import { ACCEPTED_FILE_TYPES, useInlineUpload } from "./useInlineUpload";
import "./markdown-editor.css";

export interface MarkdownEditorHandle {
	textarea: () => HTMLTextAreaElement | null;
}

export interface MarkdownEditorProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	autoFocus?: boolean;
	/** Fires when focus leaves the editor, ignoring its own menus and file picker. */
	onBlur?: () => void;
	/** "default": GitHub-style framed editor. "floating": bare textarea, toolbar pops above on focus. */
	variant?: "default" | "floating";
	/** Take pasted/dropped/picked files instead of uploading them inline. */
	onFiles?: (files: File[]) => void;
	/** Extra textarea handlers, e.g. an autocomplete that must see keys first. */
	textareaProps?: Pick<
		TextareaHTMLAttributes<HTMLTextAreaElement>,
		"onKeyDownCapture" | "onSelect"
	>;
	className?: string;
	ref?: Ref<MarkdownEditorHandle>;
}

export function MarkdownEditor({
	value,
	onChange,
	placeholder = "Add your comment here...",
	autoFocus = false,
	onBlur,
	variant = "default",
	onFiles,
	textareaProps,
	className,
	ref,
}: MarkdownEditorProps) {
	const [tab, setTab] = useState<EditorTab>("write");
	const editorRef = useRef<RefMDEditor>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { resolvedTheme } = useTheme();
	const floating = variant === "floating";

	const textarea = () => editorRef.current?.textarea ?? null;
	useImperativeHandle(ref, () => ({ textarea }), []);

	const run = (command: ICommand) =>
		editorRef.current?.commandOrchestrator?.executeCommand(command);
	const insert = (text: string, options?: { block?: boolean }) =>
		run(insertCommand(text, options));
	const openFilePicker = () => fileInputRef.current?.click();
	const { upload, uploading } = useInlineUpload(insert);
	const takeFiles = (files: File[]) => {
		if (files.length === 0) return;
		if (onFiles) onFiles(files);
		else void upload(files);
	};

	function handleBlur(e: FocusEvent<HTMLDivElement>) {
		// Window lost focus (file picker, tab switch): the user has not left.
		if (!document.hasFocus()) return;
		const next = e.relatedTarget as HTMLElement | null;
		if (next && (e.currentTarget.contains(next) || next.closest(`[${POPUP_ATTR}]`)))
			return;
		onBlur?.();
	}

	const tabs = (
		<EditorTabs
			value={tab}
			onChange={setTab}
			variant={floating ? "pill" : "header"}
		/>
	);
	const toolbar = tab === "write" && (
		<MarkdownToolbar
			onCommand={run}
			onInsert={insert}
			onAttach={openFilePicker}
			onMenuClose={() => textarea()?.focus()}
			getText={() => textarea()?.value ?? ""}
		/>
	);

	return (
		<div
			data-variant={variant}
			className={cn(
				"md-editor",
				floating
					? "group relative"
					: "overflow-hidden rounded-md border border-border-strong bg-surface",
				className,
			)}
			onBlur={onBlur ? handleBlur : undefined}
			onDragOver={(e) => {
				if (tab === "write" && e.dataTransfer.types.includes("Files"))
					e.preventDefault();
			}}
			onDrop={(e) => {
				if (tab !== "write" || e.dataTransfer.files.length === 0) return;
				e.preventDefault();
				takeFiles(Array.from(e.dataTransfer.files));
			}}
		>
			{floating ? (
				// Shown while the editor has focus, a toolbar menu is open, or
				// previewing (so the user can switch back to Write).
				<div
					className={cn(
						"absolute bottom-full left-0 z-20 mb-1 flex max-w-full flex-wrap items-center gap-1 rounded-md border border-border bg-surface p-0.5 shadow-md transition-opacity",
						tab === "preview"
							? "opacity-100"
							: "pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 has-[[data-state=open]]:pointer-events-auto has-[[data-state=open]]:opacity-100",
					)}
				>
					{tabs}
					{toolbar && <span aria-hidden className="h-5 w-px bg-border" />}
					{toolbar}
				</div>
			) : (
				<div className="flex flex-wrap items-end justify-between gap-x-2 border-b border-border bg-muted-subtle/50 pr-2">
					{tabs}
					{toolbar}
				</div>
			)}

			<div className={cn(!floating && "p-2")}>
				{/* Kept mounted while previewing so textarea undo history survives. */}
				<div className={cn(tab !== "write" && "hidden")}>
					<MDEditor
						ref={editorRef}
						value={value}
						onChange={(next) => onChange(next ?? "")}
						preview="edit"
						hideToolbar
						highlightEnable={false}
						visibleDragbar={false}
						height="auto"
						commands={SHORTCUT_COMMANDS}
						extraCommands={[]}
						autoFocus={autoFocus}
						autoFocusEnd={autoFocus}
						data-color-mode={resolvedTheme}
						textareaProps={{
							...textareaProps,
							placeholder,
							onPaste: (e) => {
								// Rich copies (Office, Sheets) carry text too; paste that instead.
								if (e.clipboardData.types.includes("text/plain")) return;
								const files = Array.from(e.clipboardData.files);
								if (files.length === 0) return;
								e.preventDefault();
								takeFiles(files);
							},
						}}
					/>
				</div>
				{tab === "preview" && (
					<div
						className={cn(
							"px-3 py-2",
							floating
								? "min-h-16 rounded-md border border-border-strong"
								: "min-h-[120px]",
						)}
					>
						{value.trim() ? (
							<MarkdownPreview source={value} />
						) : (
							<p className="text-sm text-muted">Nothing to preview</p>
						)}
					</div>
				)}
			</div>

			{!floating && (
				<MarkdownFooter uploading={uploading} onAttach={openFilePicker} />
			)}
			<input
				ref={fileInputRef}
				type="file"
				multiple
				accept={ACCEPTED_FILE_TYPES}
				className="hidden"
				onChange={(e) => {
					takeFiles(Array.from(e.target.files ?? []));
					e.target.value = "";
				}}
			/>
		</div>
	);
}
